import { NgClass, NgStyle } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';

type ButtonType = 'Button' | 'Submit' | 'Reset';

const ButtonColor = {
  Primary: {
    Default: '!bg-primary-800 !text-primary-50',
    Disabled: '!bg-primary-200 !text-primary-400',
    Hover: 'hover:!bg-primary-1000 hover:!text-primary-50',
  },
  Secondary: {
    Default: '!bg-search-buttonColor !text-primary-50',
    Disabled: '!bg-primary-200 !text-primary-400',
    Hover: 'hover:!bg-secondary-800 hover:!text-primary-50',
  },
  Tertiary: {
    Default: 'border-ternary-600 border-2 !text-ternary-600',
    Disabled: 'border-primary-200 border-2 !text-primary-500',
    Hover: 'hover:!bg-ternary-600 hover:!text-primary-50',
  },
};

const ButtonSize = {
  XSmall: 'h-[20px] text-3xs',
  Small: 'h-[28px] text-xs',
  Medium: 'h-[36px] text-md',
  Large: 'h-[44px] text-sm',
  XLarge: 'h-[52px] text-md',
  Modal: 'h-[36px] text-2xs',
};

@Component({
  selector: 'app-button',
  standalone: true,
  imports: [NgClass, NgStyle],
  templateUrl: './button.component.html',
  styleUrl: './button.component.css',
})
export class ButtonComponent {
  @Input() color: keyof typeof ButtonColor = 'Primary';
  @Input() text: string = 'Button';
  @Input() size: keyof typeof ButtonSize = 'XLarge';
  @Input() disabled: boolean = false;
  @Input() type: ButtonType = 'Button';
  @Input() icon!: string;
  @Input() placeIcon: string = 'left';
  @Input() width!: number;

  @Output() buttonClick = new EventEmitter<Event>();

  onClick(event: Event) {
    if (this.disabled) return;
    this.buttonClick.emit(event);
  }

  get colorVariantClass(): string {
    const variant = this.disabled ? 'Disabled' : 'Default';
    return ButtonColor[this.color][variant];
  }

  get hoverClass(): string {
    if (this.disabled) return '!cursor-not-allowed';
    return ButtonColor[this.color]?.Hover ?? '';
  }

  get sizeClass(): string {
    return ButtonSize[this.size];
  }

  get buttonStyle(): { [key: string]: string } {
    return this.width ? { width: `${this.width}px` } : {};
  }

  get buttonClasses(): string[] {
    let paddingY = '!py-[11px]';
    if (this.color === 'Tertiary' && !this.disabled) {
      paddingY = '!py-[9px]';
    }
    return [
      this.colorVariantClass,
      this.sizeClass,
      `!flex !items-center !justify-center !rounded-[36px] !px-4 ${paddingY} !font-medium cursor-pointer !transition-all !duration-200`,
    ];
  }

  get hoverStyle(): { [klass: string]: boolean } {
    return { [this.hoverClass]: false };
  }

  get fullButtonClass(): string {
    return `${this.buttonClasses.join(' ')} ${this.hoverClass}`;
  }
}
